import { useState } from "react";
import { LifeBuoy, Mail, MessageCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const FAQS = [
  {
    q: "How long do withdrawals take?",
    a: "Withdrawals are processed manually within 24 hours during business days. You can track the request status in your Wallet → Pending requests.",
  },
  {
    q: "Is my money safe?",
    a: "Yes. Top-ups go through Stripe (PCI-DSS Level 1) — we never see your card. Bank details for withdrawals are encrypted. Withdrawals require your Wallet PIN.",
  },
  {
    q: "How does the Ajo group system work?",
    a: "Create a group with name + amount + frequency. Members join with the group code. The Ajo Planner generates a fair rotating payout schedule. We auto-remind everyone 48h before each due date, and missed cycles trigger penalties + trust-score drops automatically.",
  },
  {
    q: "What is Zuntria Premium?",
    a: "£14.99/month for the complete Zuntria execution system — unlimited AI, full Truffl, Get Paid This Week, Ajo circles and all 15 AI capabilities. Cancel any time — access continues to the end of your paid period.",
  },
  {
    q: "Why am I limited to 20 chat messages?",
    a: "Free users get 20 AI messages per day. Upgrade to Premium for unlimited messages.",
  },
  {
    q: "How do I get paid for marketplace sales?",
    a: "Buyers contact you directly via the WhatsApp number on your listing. You handle payment and delivery with the buyer. Zuntria does not take a cut of marketplace sales (yet — coming soon).",
  },
  {
    q: "What happens if I miss an Ajo contribution?",
    a: "Your trust score drops by 20 points and a default is logged. If your group has insurance enabled and the pool has funds, the contribution is covered automatically — otherwise the cycle continues without you.",
  },
  {
    q: "Can I export my CV/letter as a PDF?",
    a: "Yes. Open any document and tap 'Download PDF'. PDFs are ATS-friendly and ready to send.",
  },
  {
    q: "How do referrals work?",
    a: "Share your referral link. When a friend signs up and verifies their email, you get £1 in your wallet + £1 in referral earnings + 1 point bonus. Your friend gets £0.50 cashback to start.",
  },
  {
    q: "How do I contact support?",
    a: "Email support@zuntria.ai or message us on WhatsApp (+44 7587 831674). We reply within 24 hours.",
  },
];

export default function HelpPage() {
  const [open, setOpen] = useState(0);
  return (
    <div className="mx-auto max-w-3xl px-6 py-12 md:px-10 md:py-16">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Support</p>
      <h1 className="mt-2 font-display text-4xl font-medium tracking-tight"><LifeBuoy className="mr-2 inline h-7 w-7 text-primary" /> Help &amp; FAQ</h1>
      <p className="mt-3 text-muted-foreground">Quick answers to common questions. Still stuck? Drop us a line.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild data-testid="help-email-btn"><a href="mailto:support@zuntria.ai"><Mail className="mr-1 h-4 w-4" /> support@zuntria.ai</a></Button>
        <Button asChild variant="outline" data-testid="help-whatsapp-btn"><a href="https://wa.me/447587831674" target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp us</a></Button>
      </div>

      <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-card">
        {FAQS.map((f, i) => (
          <button
            key={i}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="block w-full px-5 py-4 text-left transition-colors hover:bg-secondary/50"
            data-testid={`faq-${i}`}
          >
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium">{f.q}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </div>
            {open === i && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>}
          </button>
        ))}
      </div>
    </div>
  );
}
